import { getErrorMessage } from '@/lib/utils'
import { ErrorBoundary } from '@sentry/react'
import {
  Suspense,
  type ComponentProps,
  type ComponentType,
  type ReactElement,
  type FunctionComponent,
} from 'react'

interface SuspenseFallbackOptions {
  loading?: ComponentType
  error?: ComponentProps<typeof ErrorBoundary>['fallback']
}

const withSuspense = <Props extends object>(
  WrappedComponent: (props: Props) => ReturnType<FunctionComponent<Props>>,
  fallbackOptions: SuspenseFallbackOptions = {}
): ((props: Props) => ReactElement) => {
  let {
    loading: LoadingFallback = () => (
      <div className="text-muted-foreground text-sm">Loading...</div>
    ),
    error: ErrorFallback,
  } = fallbackOptions

  ErrorFallback ??= ({ error }) => <div>Error: {getErrorMessage(error)}</div>

  return function WithSuspenseWrapper(props: Props) {
    return (
      <ErrorBoundary fallback={ErrorFallback}>
        <Suspense fallback={<LoadingFallback />}>
          <WrappedComponent {...props} />
        </Suspense>
      </ErrorBoundary>
    )
  }
}

export default withSuspense
